/**
 * Lightweight i18n helper for Konnect operator landing pages.
 *
 * No external library (we don't need i18next on every page — that would
 * bloat the bundle by ~100KB). Instead we ship a small lookup table for
 * the half-dozen default UI strings that aren't editable by operators
 * (Submit, Loading, Required field, error messages).
 *
 * The actual page content (headlines, body, CTAs) is editable by each
 * operator in their own language via the landing-page builder, so we
 * only need to localize the "chrome" around it.
 *
 * Usage:
 *   import { t, applyDirection } from '../lib/i18n';
 *   const submit = t('submit', locale);
 *   applyDirection(locale);  // sets <html dir="rtl"> for Arabic/Hebrew
 *
 * Supported locales today: en (default), ar (Arabic), tl (Tagalog).
 * Adding a new locale: just add a column to STRINGS below.
 */

const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur']);

const STRINGS = {
  // Forms
  submit: { en: 'Submit', ar: 'إرسال', tl: 'Ipasa' },
  sending: { en: 'Sending…', ar: 'جارٍ الإرسال…', tl: 'Pinapadala…' },
  thank_you: { en: 'Thank you!', ar: 'شكرًا لك!', tl: 'Salamat!' },
  required_field: { en: 'This field is required', ar: 'هذا الحقل مطلوب', tl: 'Kailangan ang field na ito' },
  invalid_email: { en: 'Please enter a valid email', ar: 'يرجى إدخال بريد إلكتروني صالح', tl: 'Maglagay ng tamang email' },

  // CTAs
  learn_more: { en: 'Learn more', ar: 'اعرف المزيد', tl: 'Alamin pa' },
  get_started: { en: 'Get started', ar: 'ابدأ الآن', tl: 'Magsimula' },
  contact_us: { en: 'Contact us', ar: 'تواصل معنا', tl: 'Makipag-ugnayan' },
  book_now: { en: 'Book now', ar: 'احجز الآن', tl: 'Mag-book ngayon' },

  // Loading / error
  loading: { en: 'Loading…', ar: 'جارٍ التحميل…', tl: 'Naglo-load…' },
  error_generic: { en: 'Something went wrong. Please try again.', ar: 'حدث خطأ. يرجى المحاولة مرة أخرى.', tl: 'May problema. Subukan ulit.' },
  page_not_found: { en: 'Page not found', ar: 'الصفحة غير موجودة', tl: 'Hindi nahanap ang pahina' },

  // Footer
  powered_by: { en: 'Powered by', ar: 'مدعوم من', tl: 'Pinapagana ng' },

  // Field labels
  name: { en: 'Name', ar: 'الاسم', tl: 'Pangalan' },
  email: { en: 'Email', ar: 'البريد الإلكتروني', tl: 'Email' },
  phone: { en: 'Phone', ar: 'رقم الهاتف', tl: 'Telepono' },
  message: { en: 'Message', ar: 'الرسالة', tl: 'Mensahe' },
};

export function t(key, locale = 'en') {
  const normalized = (locale || 'en').toLowerCase().split('-')[0];
  const row = STRINGS[key];
  if (!row) return key;
  return row[normalized] || row.en || key;
}

export function isRTL(locale) {
  if (!locale) return false;
  return RTL_LOCALES.has(locale.toLowerCase().split('-')[0]);
}

/**
 * Apply text direction to the current document for the given locale.
 * Idempotent — safe to call on every render.
 * Sets `lang` too so screen readers + browser spell-check behave.
 */
export function applyDirection(locale) {
  if (typeof document === 'undefined') return;
  const normalized = (locale || 'en').toLowerCase().split('-')[0];
  const dir = isRTL(normalized) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', normalized);
}

/**
 * Resolve the locale a landing page should render in, in priority order:
 *   1. Explicit `?lang=` query param (lets operators preview in any language)
 *   2. The page's stored `landing_locale` field (set by operator in builder)
 *   3. Browser `navigator.language` if matched to a supported locale
 *   4. 'en' default
 */
export function resolveLocale(pageLocale) {
  if (typeof window !== 'undefined') {
    const qsLocale = new URLSearchParams(window.location.search).get('lang');
    if (qsLocale && STRINGS.submit[qsLocale.toLowerCase().split('-')[0]]) {
      return qsLocale.toLowerCase().split('-')[0];
    }
  }
  if (pageLocale && STRINGS.submit[pageLocale.toLowerCase().split('-')[0]]) {
    return pageLocale.toLowerCase().split('-')[0];
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const navLang = navigator.language.toLowerCase().split('-')[0];
    if (STRINGS.submit[navLang]) return navLang;
  }
  return 'en';
}

export const SUPPORTED_LOCALES = ['en', 'ar', 'tl'];
